import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import Seo from '../../components/Seo';
import AppStoreButtons from '../../components/release/AppStoreButtons';
import { PLATFORM } from '../../config/platform';
import './SearchLandingPage.css';

const PAGES = {
  players: {
    path: '/find-pool-players-near-me',
    title: 'Find Pool Players Near Me | Meet Local Billiards Players',
    description: 'Find nearby pool and billiards players, view real player profiles, chat, organize an in-person match, join teams, and connect with your local billiards community on Pool 4 Real.',
    kicker: 'Pool player finder and social network',
    h1: 'Find pool players nearby and organize a real match.',
    intro: 'Pool 4 Real helps people who play pool in real life discover nearby players, learn about their experience and interests, start a conversation, and arrange a game at a local pool hall or billiards venue.',
    action: 'Find nearby players',
    to: '/players',
    sectionTitle: 'Meet the right players and make the game happen',
    body: 'A player finder is more useful when it leads to a real table. Pool 4 Real combines local discovery with player profiles, messaging, teams, tournaments, and match tools so a new connection can become an actual game.',
    bullets: [
      'Discover visible pool and billiards player profiles near your location',
      'Message another player before arranging an in-person match',
      'Share your playing interests, experience, equipment, and community activity',
      'Connect through teams, tournaments, Pockits, and Race Room sessions',
    ],
    related: [
      ['/organize-pool-matches', 'How to organize pool matches'],
      ['/pool-scorekeeper', 'Pool race counter and scorekeeper'],
      ['/billiards-community', 'Billiards social networking app'],
    ],
    faq: [
      ['How do I find pool players near me?', 'Open Nearby Players, sign in, complete your player profile, and allow location access. Pool 4 Real can then show visible billiards player profiles around your area.'],
      ['Can I message a local pool player?', 'Yes. Signed-in users with complete player profiles can start and continue conversations with other players, subject to each user’s privacy and safety settings.'],
      ['Can I use Pool 4 Real to arrange an actual pool match?', 'Yes. Player discovery and messaging are designed to help people coordinate real-world games at pool halls, clubs, bars, or other billiards venues.'],
      ['Does Pool 4 Real include simulated billiards practice?', 'Yes. The Android app includes the P4R Engine Practice Room for digital shot practice and training, while the rest of Pool 4 Real remains connected to real players, venues, matches, teams and tournaments.'],
    ],
  },
  matches: {
    path: '/organize-pool-matches',
    title: 'Organize Pool Matches | Find Players, Chat & Keep Score',
    description: 'Organize real pool and billiards matches with nearby players. Connect, chat, choose a venue, set up a race, and track scores with Pool 4 Real Race Room.',
    kicker: 'Real-world match organization',
    h1: 'Turn a nearby player connection into a real pool match.',
    intro: 'Pool 4 Real brings together the steps that usually happen across several apps: find a player, start a conversation, choose where to play, set a race format, and keep the match score.',
    action: 'Meet players near you',
    to: '/players',
    sectionTitle: 'Everything around the match, in one connected account',
    body: 'Use player profiles and conversations to coordinate the game, discover pool halls on the map, then open Race Room when the match begins. Teams and tournament tools are available when the competition grows beyond a single set.',
    bullets: [
      'Find local players who are interested in real-world pool and billiards',
      'Use direct conversations to coordinate availability and match details',
      'Discover pool halls, venues, and billiards businesses near you',
      'Set race targets, count racks, track time, and save match history in Race Room',
    ],
    related: [
      ['/find-pool-players-near-me', 'Find nearby pool players'],
      ['/pool-halls-near-me', 'Find a pool hall'],
      ['/pool-match-tracker', 'Track the match'],
    ],
    faq: [
      ['Can Pool 4 Real help me find an opponent?', 'Yes. Nearby Players is designed to help you discover and contact other real-world pool and billiards players in your area.'],
      ['Can we decide the race format in the app?', 'Race Room supports configurable race targets and multiple scoring approaches for common pool and billiards match situations.'],
      ['Can I save the result?', 'Signed-in users can save supported Race Room sessions and completed match information to their P4R account.'],
    ],
  },
  tournaments: {
    path: '/pool-tournaments-near-me',
    title: 'Pool Tournaments Near Me | Find, Join & Manage Events',
    description: 'Find nearby pool tournaments, enter as a player or team, check in, follow brackets and results, or organize a real billiards event with Pool 4 Real.',
    kicker: 'Tournament discovery and operations',
    h1: 'Find a pool tournament near you—or organize the event.',
    intro: 'Pool 4 Real connects real billiards events with players, teams, venues, and tournament managers. Discover competitions by location and discipline, enter supported events, check in, follow the bracket, and keep up with announcements.',
    action: 'Explore pool tournaments',
    to: '/tournaments',
    sectionTitle: 'Tournament tools for players and organizers',
    body: 'Managers can create and operate tournaments from one connected system while players can discover events, register, receive updates, and follow progress. The tools are built around real competition at physical venues.',
    bullets: [
      'Player, guest, and supported team entry workflows',
      'Check-in windows, walk-ins, seeds, and participant management',
      'Single-elimination bracket generation and table assignments',
      'Match scoring, winner advancement, announcements, and results',
    ],
    related: [
      ['/pool-teams', 'Create or join a pool team'],
      ['/pool-scorekeeper', 'Use Race Room for match scoring'],
      ['/pool-halls-near-me', 'Discover billiards venues'],
    ],
    faq: [
      ['Can I find pool tournaments near my location?', 'Pool 4 Real can surface tournament information based on the event details and location provided by organizers. Availability depends on tournaments listed in your area.'],
      ['Can tournament managers create brackets?', 'Yes. Supported manager workflows include participant management, seeding, single-elimination bracket generation, table assignments, scoring, and winner advancement.'],
      ['Can players withdraw from a tournament?', 'Eligible entries can be withdrawn with a confirmation that explains the consequence, subject to the tournament’s current status and rules.'],
    ],
  },
  halls: {
    path: '/pool-halls-near-me',
    title: 'Pool Halls Near Me | Find Billiards Venues & Events',
    description: 'Find pool halls, billiards clubs, venues, tournaments, retailers, cue services, and pool-related businesses near you with the Pool 4 Real map.',
    kicker: 'Pool hall and billiards venue discovery',
    h1: 'Find places to play pool and the businesses behind the sport.',
    intro: 'Use the Pool 4 Real map to discover real billiards venues and businesses, including pool halls, clubs, retailers, cue services, instructors, event hosts, and other organizations connected to pool and billiards.',
    action: 'Open the billiards map',
    to: '/map',
    sectionTitle: 'Discover the local billiards ecosystem',
    body: 'Pool 4 Real is designed to connect players not only with each other, but also with the physical places, events, products, and services that make local pool communities possible.',
    bullets: [
      'Explore individual business and venue location profiles',
      'Review available details, offerings, contact information, and hours',
      'Discover location-based events and tournaments when hosts publish them',
      'Connect player discovery, match planning, and venue discovery in one platform',
    ],
    related: [
      ['/find-pool-players-near-me', 'Find someone to play'],
      ['/pool-tournaments-near-me', 'Find a tournament'],
      ['/pool-hall-management', 'Pool hall and organizer tools'],
    ],
    faq: [
      ['Does Pool 4 Real only list pool halls?', 'No. Profiles can also represent billiards clubs, retailers, cue services, instructors, manufacturers, event hosts, and other related businesses.'],
      ['Can a business manage more than one location?', 'Yes. Pool 4 Real supports one business identity with individual location profiles where that feature is available.'],
      ['Can I find events at a venue?', 'Business events and tournaments can be associated with location profiles when organizers provide that information.'],
    ],
  },
  practice: {
    path: '/pool-practice-room',
    title: 'Pool Practice Room & Shot Training Simulator | P4R Engine Android',
    description: 'Train pool and billiards on Android with the Pool 4 Real P4R Engine Practice Room: 9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca, shot prediction, Auto Aim, drills, cue and spin control, discipline-specific physics and gameplay audio.',
    kicker: 'P4R Engine Practice Room for Android',
    h1: 'Practice pool and billiards with the P4R Engine.',
    intro: 'Pool 4 Real’s Android Practice Room is a full digital training environment built around the same sport community account. It supports multiple billiards disciplines with calibrated table, ball and pocket behavior, live physics, shot prediction, training intelligence, cue/spin controls, drills and match-tuned gameplay audio.',
    action: 'Get Pool 4 Real for Android',
    to: '/android',
    sectionTitle: 'One Practice Room, multiple billiards disciplines',
    body: 'The current Android reference implementation includes 9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca and additional modes. The engine keeps live gameplay separate from prediction and solver simulations so training tools can remain responsive without changing the authoritative shot result.',
    bullets: [
      '9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca and additional supported modes',
      'Discipline-specific table dimensions, balls, pockets, cushions, friction and restitution',
      'Shot prediction, Auto Aim, guided coaching, drills, ball-in-hand and training scorecards',
      'Cue-tip offset, spin, elevation, power, breaks and match-tuned procedural gameplay audio',
    ],
    related: [
      ['/pool-scorekeeper', 'Run a live match in Race Room'],
      ['/find-pool-players-near-me', 'Find real players nearby'],
      ['/pool-tournaments-near-me', 'Find pool tournaments'],
    ],
    faq: [
      ['What is the P4R Engine?', 'P4R Engine is Pool 4 Real’s Android Practice Room simulation and training engine for billiards disciplines, shot planning, drills and guided practice.'],
      ['Which games are supported?', 'The Android mode selector starts with 9 Ball, 8 Ball, 3 Cushion and Brazilian Sinuca, followed by additional pool and billiards modes supported by the current release.'],
      ['Does Practice Room use the same physics for every game?', 'No. The Android reference implementation uses discipline-specific equipment and physics profiles, including a dedicated Brazilian Sinuca table and 54 mm ball profile.'],
      ['Does Practice Room have sound?', 'Yes. The current Android engine includes match-tuned gameplay audio for cue strikes, ball collisions, rails, pocket jaws, pockets and scratches, with different responses by discipline and shot conditions.'],
    ],
  },
  scorekeeper: {
    path: '/pool-scorekeeper',
    title: 'Pool Scorekeeper & Race Counter | Track Real Billiards Matches',
    description: 'Use Pool 4 Real Race Room as a pool scorekeeper and race counter. Track racks, targets, points, innings, timers, turns, match history, and live scores for real billiards matches.',
    kicker: 'Race Room pool scorekeeper',
    h1: 'Keep the race, score, and match flow at the real table.',
    intro: 'Race Room is Pool 4 Real’s advanced live match system. Use it beside the table to count racks, manage asymmetric race targets, track points or innings, run shot and match clocks, control player turns, save sessions, and open a full-screen table display.',
    action: 'Open Race Room',
    to: '/race-room',
    sectionTitle: 'A flexible race counter built around live play',
    body: 'Whether you are playing a friendly set, practicing, keeping a longer race, or supporting organized competition, Race Room gives players and scorekeepers a clear shared view of the match.',
    bullets: [
      'Track race targets, racks won, points, innings, current runs, and high runs',
      'Use shot-timer, match-clock, pause, reset, and undo controls',
      'Support player targets, starting scores, guests, doubles, and configurable match flow',
      'Use live-display and supported shared-scoring modes for better table-side visibility',
    ],
    related: [
      ['/pool-match-tracker', 'Pool match tracker'],
      ['/organize-pool-matches', 'Organize a real pool match'],
      ['/pool-tournaments-near-me', 'Tournament scoring and operations'],
    ],
    faq: [
      ['What is a pool race counter?', 'A race counter tracks how many racks or games each player has won toward a target, such as a race to 5, 7, or another number.'],
      ['Is Race Room a video game?', 'No. Race Room is a table-side scorekeeping and match-management tool for pool and billiards played in the real world.'],
      ['Can Race Room track more than racks?', 'Supported modes can also track points, innings, runs, timers, player turns, starting scores, and different player targets.'],
      ['Can Race Room save match history?', 'Signed-in users can save supported Race Room sessions and completed results in the shared P4R data model.'],
    ],
  },
  tracker: {
    path: '/pool-match-tracker',
    title: 'Pool Match Tracker | Racks, Scores, Timers & Match History',
    description: 'Track real pool matches with rack counts, race targets, points, innings, timers, turns, undo history, and saved sessions using Pool 4 Real Race Room.',
    kicker: 'Live billiards match tracking',
    h1: 'Track the match without losing focus on the table.',
    intro: 'Pool 4 Real Race Room keeps important match information visible while players concentrate on real play. Record the score, manage the clock, review recent actions, and keep a clear view of who is ahead.',
    action: 'Start tracking a match',
    to: '/race-room',
    sectionTitle: 'A shared match view for players and scorekeepers',
    body: 'Race Room is designed for table-side use on phones, tablets, browsers, and larger displays. The goal is simple: make the real match easier to follow and record.',
    bullets: [
      'Large player scores and race targets for quick table-side reading',
      'Match clock and shot timer controls for structured play',
      'Undo and event history to recover from scoring mistakes',
      'Session saving, completion, history, and live-display options',
    ],
    related: [
      ['/pool-scorekeeper', 'Pool race counter and scorekeeper'],
      ['/find-pool-players-near-me', 'Find a player for your next match'],
      ['/pool-tournaments-near-me', 'Pool tournament tools'],
    ],
    faq: [
      ['Can I track a race to a custom number?', 'Yes. Race Room supports configurable targets for supported race formats.'],
      ['Can I correct an accidental score?', 'Race Room includes undo and event-history concepts for supported session actions.'],
      ['Can spectators see the score?', 'A display-oriented Race Room view can present the match more clearly on a larger screen, subject to the available platform controls.'],
    ],
  },
  teams: {
    path: '/pool-teams',
    title: 'Pool Teams | Create, Join & Manage a Billiards Team',
    description: 'Create or join a real pool team, manage members and roles, approve requests, share announcements, and enter supported billiards tournaments with Pool 4 Real.',
    kicker: "Buck's House teams",
    h1: 'Build and manage a pool team that is ready to play.',
    intro: 'Pool 4 Real gives real-world billiards teams a connected home for discovery, join requests, roster management, roles, announcements, and supported tournament participation.',
    action: 'Explore pool teams',
    to: '/teams',
    sectionTitle: 'Team organization without scattered group chats and lists',
    body: 'Keep the roster, leadership, requests, communication, and competition identity together. Owners and admins can manage the team while members stay connected to announcements and events.',
    bullets: [
      'Create public, private, open, or approval-required teams',
      'Manage owner, admin, and member roles with confirmations',
      'Review join requests, roster activity, removals, and bans',
      'Publish team announcements and connect the roster to tournaments',
    ],
    related: [
      ['/pool-tournaments-near-me', 'Enter pool tournaments'],
      ['/find-pool-players-near-me', 'Meet potential teammates'],
      ['/billiards-community', 'Join the broader billiards community'],
    ],
    faq: [
      ['Can a pool team require approval?', 'Yes. Team owners can choose an open joining flow or require players to submit a request.'],
      ['Can team owners assign admins?', 'Supported team management allows owners to promote active members to admin and return them to member status when needed.'],
      ['Can a team enter tournaments?', 'Pool 4 Real includes supported team-entry concepts for compatible tournaments and organizer workflows.'],
    ],
  },
  business: {
    path: '/pool-hall-management',
    title: 'Pool Hall Management & Billiards Business Tools | Pool 4 Real',
    description: 'Connect your pool hall or billiards business with players. Manage locations, publish events, support tournaments, create posts and ads, and build local visibility with Pool 4 Real.',
    kicker: 'Tools for pool halls and billiards businesses',
    h1: 'Connect your pool hall or billiards business to the players around it.',
    intro: 'Pool 4 Real helps billiards businesses participate directly in the community. Create location profiles, publish relevant information, promote events, support tournament activity, communicate through posts, and build a stronger local presence.',
    action: 'Explore businesses and venues',
    to: '/map',
    sectionTitle: 'Business tools built around real billiards activity',
    body: 'The platform is designed for pool halls, clubs, retailers, instructors, cue services, manufacturers, tournament hosts, and other businesses connected to the sport.',
    bullets: [
      'Manage a business identity with supported individual location profiles',
      'Publish location details, hours, offerings, contact information, and media',
      'Create events, support bookings, and connect locations to tournament activity',
      'Use business posts and supported advertising tools to reach the P4R community',
    ],
    related: [
      ['/pool-halls-near-me', 'Pool hall discovery'],
      ['/pool-tournaments-near-me', 'Tournament organization'],
      ['/billiards-community', 'The Pool 4 Real community'],
    ],
    faq: [
      ['Who can use Pool 4 Real business tools?', 'Pool halls, billiards clubs, retailers, instructors, repair services, manufacturers, event organizers, and other legitimate pool-related businesses may fit the supported business profile model.'],
      ['Can I manage multiple pool hall locations?', 'Pool 4 Real supports a shared business identity with individual location records where the feature is enabled.'],
      ['Can a venue publish events?', 'Supported business workflows include location-based events and related management tools.'],
    ],
  },
  marketplace: {
    path: '/buy-sell-pool-equipment',
    title: 'Buy & Sell Pool Equipment | Cues, Cases, Tables & Gear',
    description: 'Browse and list pool cues, cases, tables, balls, racks, accessories, and billiards equipment with player-connected listings in Pool 4 Real Shoparound.',
    kicker: 'Shoparound pool equipment marketplace',
    h1: 'Buy and sell pool equipment inside a billiards community.',
    intro: 'Shoparound connects billiards listings with real P4R profiles and conversations, helping players discover cues, cases, tables, balls, accessories, and other pool equipment.',
    action: 'Browse pool equipment',
    to: '/shoparound',
    sectionTitle: 'Marketplace listings connected to the community',
    body: 'Instead of separating equipment from the people who use it, Shoparound keeps listings close to player profiles, local discovery, messaging, reporting, and blocking tools.',
    bullets: [
      'Create listings with up to three supported images',
      'Use location-aware discovery when listing information is available',
      'Describe seller payment, delivery, or meetup preferences',
      'Start listing conversations and use supported safety controls',
    ],
    related: [
      ['/billiards-community', 'Join the billiards community'],
      ['/find-pool-players-near-me', 'Meet local pool players'],
      ['/pool-halls-near-me', 'Find billiards retailers and services'],
    ],
    faq: [
      ['What pool equipment can I list?', 'Relevant items may include cues, cases, tables, balls, racks, accessories, training tools, and other legitimate pool or billiards equipment.'],
      ['Does Pool 4 Real process marketplace payments?', 'Shoparound lets sellers describe their preferred payment, delivery, or meetup method. Users should use good judgment and safe transaction practices.'],
      ['Can I report a listing or block a seller?', 'Signed-in users can access supported reporting and blocking tools for marketplace safety.'],
    ],
  },
  community: {
    path: '/billiards-community',
    title: 'Billiards Social Network | Players, Matches, Venues & Tournaments',
    description: 'Join a real-world billiards social network for nearby players, match organization, pool scoring, teams, tournaments, venues, posts, messages, businesses, and equipment.',
    kicker: 'The social network for real-world billiards',
    h1: 'A pool and billiards community built around actually playing the sport.',
    intro: 'Pool 4 Real brings nearby player discovery, profiles, Pockits, messages, match organization, Race Room scoring, teams, tournaments, venues, businesses, bookings, and equipment listings into one connected platform.',
    action: 'Join Pool 4 Real',
    to: '/login',
    sectionTitle: 'A connected billiards platform for real play and digital practice',
    body: 'Pool 4 Real is social networking and sports utility software for real players, real venues, and real matches. It helps the community connect before the game, manage activity during play, and stay involved afterward.',
    bullets: [
      'Nearby player discovery, profiles, Pockits, and direct conversations',
      'Real-world match organization and Race Room scorekeeping',
      'Teams, tournaments, pool halls, events, bookings, and business tools',
      'Shoparound listings for cues, tables, accessories, and billiards gear',
    ],
    related: [
      ['/find-pool-players-near-me', 'Find pool players nearby'],
      ['/organize-pool-matches', 'Organize a real match'],
      ['/pool-scorekeeper', 'Use the Race Room scorekeeper'],
    ],
    faq: [
      ['Does Pool 4 Real include a billiards simulator?', 'Yes. The Android app includes the P4R Engine Practice Room for multi-discipline digital practice and shot training alongside Pool 4 Real’s real-world social, match, team, tournament and venue tools.'],
      ['Who is Pool 4 Real for?', 'It is built for casual and competitive players, teams, tournament organizers, pool halls, clubs, retailers, instructors, service providers, and the wider billiards community.'],
      ['What makes Pool 4 Real different from a basic player finder?', 'It connects player discovery with messaging, match organization, Race Room scoring, teams, tournaments, venues, social activity, business tools, and equipment listings.'],
      ['Is Pool 4 Real available on mobile?', 'Yes. Official Pool 4 Real apps are available for iPhone and Android, with supported features also available on the web.'],
    ],
  },
};

export { PAGES };

export default function SearchLandingPage({ pageKey }) {
  const page = PAGES[pageKey];
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map(([question, answer]) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.title,
    description: page.description,
    url: `${PLATFORM.siteUrl}${page.path}`,
    isPartOf: { '@type': 'WebSite', name: PLATFORM.name, url: PLATFORM.siteUrl },
    about: {
      '@type': 'Thing',
      name: page.kicker,
      description: page.intro,
    },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Pool 4 Real', item: PLATFORM.siteUrl },
      { '@type': 'ListItem', position: 2, name: page.kicker, item: `${PLATFORM.siteUrl}${page.path}` },
    ],
  };

  return (
    <main className="p4r-search-landing">
      <Seo title={page.title} description={page.description} path={page.path} schema={[pageSchema, breadcrumbSchema, faqSchema]} />
      <section className="p4r-search-hero">
        <p>{page.kicker}</p>
        <h1>{page.h1}</h1>
        <div>{page.intro}</div>
        <Link to={page.to}>{page.action} <FaArrowRight /></Link>
      </section>

      <section className="p4r-search-body">
        <article>
          <h2>{page.sectionTitle}</h2>
          <p className="p4r-search-body-copy">{page.body}</p>
          <div className="p4r-search-benefits">
            {page.bullets.map((bullet) => <p key={bullet}><FaCheckCircle /> {bullet}</p>)}
          </div>
        </article>
        <aside>
          <img src="/assets/pool4real-app-icon.png" alt="Pool 4 Real social networking and match tools for billiards players" />
          <h2>Play in the real world. Stay connected on P4R.</h2>
          <p>Use the official iPhone and Android apps with the same Pool 4 Real account across supported platform features.</p>
          <AppStoreButtons compact source={pageKey} />
        </aside>
      </section>

      <section className="p4r-search-related" aria-labelledby={`${pageKey}-related-heading`}>
        <p>Continue exploring</p>
        <h2 id={`${pageKey}-related-heading`}>Related Pool 4 Real tools</h2>
        <div>
          {page.related.map(([to, label]) => <Link key={to} to={to}>{label} <FaArrowRight /></Link>)}
        </div>
      </section>

      <section className="p4r-search-faq">
        <h2>Common questions</h2>
        {page.faq.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}
      </section>
    </main>
  );
}
